import React, { useEffect, useState } from 'react';
import { errorToast, successToast } from '../../../utils/helper';
import { getEquipments, createEquipment, deleteEquipment } from '../../../api/healthAPI';
import Breadcrumb from '../../../component/common/Breadcrumb';
import Table from '../../../component/common/Table';
import Pagination from '../../../component/common/Pagination/Pagination';
import { Helmet } from 'react-helmet';
import SearchInput from '../../../component/common/Input/SearchInput';
import { PER_PAGE } from '../../../utils/constants';
import SelectMenu from '../../../component/common/SelectMenu';
import Loader from '../../../component/common/Loader';
import AddEquipmentModal from './AddEquipmentModal';
import { TableCard } from '../../../component/common/designSystem';

const pages = [{ name: 'Equipment', href: '/health/equipments' }];

const Equipment = () => {
  const [loader, setLoader] = useState(true);
  const [equipmentList, setEquipmentList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('createdAt');
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [openAddEquipment, setOpenAddEquipment] = useState(false);

  const handlePagination = async (
    pageNumber,
    pageSize,
    searchKey,
    sortByValue = sortBy,
    sortOrderValue = sortOrder
  ) => {
    setLoader(true);
    try {
      const params = {
        page: pageNumber,
        limit: pageSize,
        search: (searchKey || '').trim(),
        sortBy: sortByValue || 'createdAt',
        sortOrder: sortOrderValue || 'desc'
      };

      const response = await getEquipments(params);

      if (response?.meta?.code === 1) {
        setCurrentPage(pageNumber);
        setEquipmentList(response?.data || []);
        setTotalCount(response?.meta?.total || 0);
      } else {
        errorToast(response?.meta?.message || 'Failed to fetch equipment');
        setEquipmentList([]);
        setTotalCount(0);
      }
    } catch (error) {
      errorToast('Failed to fetch equipment');
      setEquipmentList([]);
      setTotalCount(0);
    } finally {
      setLoader(false);
    }
  };

  const handleAddEquipment = async ({ name, description }) => {
    setLoader(true);
    try {
      const payload = {
        name,
        description
      };

      const response = await createEquipment(payload);

      if (response?.meta?.code === 1) {
        successToast(response?.meta?.message || 'Equipment created successfully');
        setOpenAddEquipment(false);
        handlePagination(1, selectedPerPage?.value, searchTerm, sortBy, sortOrder);
      } else {
        errorToast(response?.meta?.message || 'Failed to create equipment');
      }
    } catch (error) {
      errorToast('Failed to create equipment');
    } finally {
      setLoader(false);
    }
  };

  const deleteHandler = async (id) => {
    setLoader(true);
    try {
      const response = await deleteEquipment(id);
      if (response?.meta?.code === 1) {
        successToast(response?.meta?.message || 'Equipment deleted successfully');
        handlePagination(1, selectedPerPage?.value, searchTerm, sortBy, sortOrder);
      } else {
        errorToast(response?.meta?.message || 'Failed to delete equipment');
      }
    } catch (error) {
      errorToast('Failed to delete equipment');
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    handlePagination(1, selectedPerPage?.value, '', sortBy, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handlePagination(1, selectedPerPage?.value, searchTerm, sortBy, sortOrder);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    handlePagination(1, perPage.value, searchTerm, sortBy, sortOrder);
  };

  const refreshTable = () =>
    handlePagination(1, selectedPerPage?.value, searchTerm, sortBy, sortOrder);

  const handleSortBy = (sortByValue) => {
    setSortBy(sortByValue);
    if (sortByValue === sortBy) {
      const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(nextOrder);
      handlePagination(currentPage, selectedPerPage?.value, searchTerm, sortByValue, nextOrder);
    } else {
      setSortOrder('asc');
      handlePagination(currentPage, selectedPerPage?.value, searchTerm, sortByValue, 'asc');
    }
  };

  const columns = [
    {
      title: 'Equipment Name',
      key: 'name',
      sortKey: 'name',
      sortable: true,
      type: 'text',
      align: 'left',
      longText: true
    },
    {
      title: 'Description',
      key: 'description',
      sortable: false,
      type: 'text',
      align: 'left',
      longText: true
    },
    {
      title: 'Created On',
      key: 'createdAt',
      sortKey: 'createdAt',
      sortable: true,
      type: 'date',
      align: 'left'
    },
    {
      title: 'Action',
      key: 'action',
      type: 'action',
      align: 'right',
      isView: false,
      isEdit: false,
      isDelete: true
    }
  ];

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Equipments | Shoorah Admin</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Equipments</h1>
      </div>

      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="sm:flex justify-between">
          <div className="flex">
            <div className="hidden sm:flex mr-3 self-center">
              <span className="self-center text-sm mr-2 text-shoorah-lightBlack dark:text-white">
                Per Page
              </span>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="self-center">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search equipment"
              />
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex justify-between">
            <div className="sm:hidden flex self-center">
              <div className="w-16">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="flex gap-y-2 flex-wrap">
              <div className="ml-3 self-center">
                <button
                  onClick={() => setOpenAddEquipment(true)}
                  type="button"
                  className="btn-primary sm:w-auto"
                >
                  Add New Equipment
                </button>
              </div>
            </div>
          </div>
        </div>

        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) =>
              handlePagination(page, selectedPerPage?.value, searchTerm, sortBy, sortOrder),
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
            columns={columns}
            data={equipmentList}
            name={'equipment_table'}
            setDeleteId={deleteHandler}
            bottomBorder={totalCount > selectedPerPage?.value}
            contentType={2}
            refreshTable={refreshTable}
            setSortBy={(sort) => handleSortBy(sort)}
            loader={loader}
            setSearchTerm={(data) => setSearchTerm(data)}
          />
        </TableCard>
      </div>

      

      {openAddEquipment && (
        <AddEquipmentModal
          open={openAddEquipment}
          onClose={() => setOpenAddEquipment(false)}
          onSubmit={handleAddEquipment}
          submitButtonText="Add Equipment"
        />
      )}
    </div>
  );
};

export default Equipment;
